import React, { useState, useEffect } from 'react';
import { useApolloClient } from '@apollo/client';
import { getProductImageUrl } from '../../../utils/imageUrl';
import {
  X, Package, Calendar, Tag, Building, Edit3,
  Image, ChevronLeft, ChevronRight, Zap, Clock, MapPin, Loader2,
  CheckCircle, QrCode, DollarSign,
} from 'lucide-react';

import {
  GET_PROMOTIONS_BY_LABORATORY,
  GET_ADDS_BY_LABORATORY,
} from '../../../api/laboratory-api';
import {
  getCategoryLabel,
  getCategoryIcon,
} from '../../../constants/product-categories';

const STATUS_MAP = {
  IN_PROGRESS: { label: 'Active',     bg: '#dcfce7', color: '#16a34a' },
  DRAFT:       { label: 'En attente', bg: '#fef9c3', color: '#d97706' },
  CLOSED:      { label: 'Terminée',   bg: '#fee2e2', color: '#dc2626' },
  APPROVED:    { label: 'Approuvée',  bg: '#dcfce7', color: '#16a34a' },
  PENDING:     { label: 'En attente', bg: '#fef9c3', color: '#d97706' },
};
const promoStatus = (s) => STATUS_MAP[s] ?? { label: s, bg: '#f3f4f6', color: '#6b7280' };

const Pill = ({ label, bg, color }) => (
  <span
    className="px-2 py-0.5 rounded-full text-xs font-semibold"
    style={{ background: bg, color }}
  >
    {label}
  </span>
);

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0">
    <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
      <Icon size={13} className="text-gray-500" />
    </div>
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-sm font-medium text-gray-800">{value ?? '—'}</p>
    </div>
  </div>
);

const ProductViewModal = ({
  product,
  isOpen,
  onClose,
  onEdit,
  laboratory,
  promotions: externalPromotions = [],
  adds: externalAdds = [],
  fetchPromotions = false,
}) => {
  const [imgIdx, setImgIdx]       = useState(0);
  const [activeTab, setActiveTab] = useState('details');
  const [promotions, setPromotions] = useState(externalPromotions);
  const [adds, setAdds]           = useState(externalAdds);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const client = useApolloClient();

  useEffect(() => {
    const shouldFetch =
      isOpen && laboratory?.id && fetchPromotions &&
      externalPromotions.length === 0 && externalAdds.length === 0;
    if (shouldFetch) {
      setLoading(true);
      Promise.all([
        client.query({ query: GET_PROMOTIONS_BY_LABORATORY, variables: { laboratoryId: laboratory.id }, fetchPolicy: 'network-only' })
          .catch(() => ({ data: { promotionsByLaboratory: [] } })),
        client.query({ query: GET_ADDS_BY_LABORATORY, variables: { laboratoryId: laboratory.id }, fetchPolicy: 'network-only' })
          .catch(() => ({ data: { addsByLaboratory: [] } })),
      ]).then(([pr, ar]) => {
        setPromotions(pr.data?.promotionsByLaboratory ?? []);
        setAdds(ar.data?.addsByLaboratory ?? []);
      }).catch(() => setError('Erreur de chargement'))
        .finally(() => setLoading(false));
    }
  }, [isOpen, laboratory?.id, fetchPromotions]);

  useEffect(() => { setPromotions(externalPromotions); }, [externalPromotions]);
  useEffect(() => { setAdds(externalAdds); }, [externalAdds]);

  if (!isOpen || !product) return null;

  const images = [
    product.image, product.images, product.imagethree,
    product.quatriemimage, product.cinquiemimage,
  ].filter(Boolean).map(getProductImageUrl);

  const productPromotions = promotions.filter(p =>
    p.products?.some(pp => pp.id === product.id || pp.productId === product.id)
  );
  const activeAds = adds.filter(ad => {
    const isForProduct = ad.productId === product.id || ad.product?.id === product.id;
    const isApproved = !ad.status || ad.status === 'APPROVED';
    const isNotExpired = new Date(ad.dateFin || ad.dateEnd) > new Date();
    return isForProduct && isApproved && isNotExpired;
  });

  const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';
  const fmtPrice = (p) =>
    new Intl.NumberFormat('fr-MA', { style: 'currency', currency: 'MAD' }).format(p);

  const TABS = [
    { id: 'details',    label: 'Détails',       icon: Package, count: null },
    { id: 'promotions', label: 'Promotions',     icon: Tag,     count: productPromotions.length },
    { id: 'ads',        label: 'Publicités',     icon: Zap,     count: activeAds.length },
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">

        {/* ── Header ────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
              {images[0]
                ? <img src={images[0]} alt="" className="w-10 h-10 rounded-xl object-cover" />
                : <Package size={18} className="text-gray-400" />}
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-gray-900 truncate">{product.name}</h2>
              <p className="text-sm font-semibold text-green-600">{fmtPrice(product.price)}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {onEdit && (
              <button onClick={() => onEdit(product)}
                className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition">
                <Edit3 size={16} />
              </button>
            )}
            <button onClick={onClose}
              className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ── Tabs ──────────────────────────────────────────────────────── */}
        <div className="flex gap-1 px-6 pt-3 border-b border-gray-100">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-green-600 text-green-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon size={14} />
              {tab.label}
              {tab.count !== null && (
                <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Body ──────────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex items-center justify-center py-8 gap-2">
              <Loader2 size={18} className="animate-spin text-green-600" />
              <span className="text-sm text-gray-500">Chargement...</span>
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* ── Details tab ─────────────────────────────────────────────── */}
          {activeTab === 'details' && (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* Image column */}
              <div className="lg:col-span-2">
                <div className="relative aspect-square bg-gray-100 rounded-2xl overflow-hidden">
                  {images.length > 0 ? (
                    <>
                      <img src={images[imgIdx]} alt={product.name} className="w-full h-full object-cover" />
                      {images.length > 1 && (
                        <>
                          <button onClick={() => setImgIdx((i) => (i - 1 + images.length) % images.length)}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-1.5 rounded-lg transition">
                            <ChevronLeft size={16} />
                          </button>
                          <button onClick={() => setImgIdx((i) => (i + 1) % images.length)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-1.5 rounded-lg transition">
                            <ChevronRight size={16} />
                          </button>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <Image size={48} className="text-gray-300" />
                    </div>
                  )}
                </div>
                {images.length > 1 && (
                  <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                    {images.map((img, i) => (
                      <button key={i} onClick={() => setImgIdx(i)}
                        className={`w-14 h-14 rounded-xl overflow-hidden border-2 flex-shrink-0 transition ${
                          i === imgIdx ? 'border-green-500' : 'border-gray-200'
                        }`}>
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Info column */}
              <div className="lg:col-span-3 space-y-4">
                {/* Price + status pills */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-2xl font-bold text-green-600">{fmtPrice(product.price)}</span>
                  {product.category && (
                    <span className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded-full">
                      {getCategoryIcon(product.category)} {getCategoryLabel(product.category)}
                    </span>
                  )}
                  {productPromotions.some(p => p.status === 'IN_PROGRESS') && (
                    <span className="px-2.5 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded-full flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                      En promotion
                    </span>
                  )}
                </div>

                {/* Description */}
                {product.description && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-400 mb-1">Description</p>
                    <p className="text-sm text-gray-700 leading-relaxed">{product.description}</p>
                  </div>
                )}

                {/* Metadata */}
                <div className="bg-white border border-gray-100 rounded-xl p-4">
                  {laboratory && <InfoRow icon={Building} label="Laboratoire" value={laboratory.nom} />}
                  {product.subcategory && <InfoRow icon={Tag} label="Sous-catégorie" value={product.subcategory} />}
                  {product.stock !== undefined && <InfoRow icon={Package} label="Stock" value={`${product.stock} unités`} />}
                  {product.createdAt && <InfoRow icon={Calendar} label="Créé le" value={fmtDate(product.createdAt)} />}
                  {product.updatedAt && <InfoRow icon={Clock} label="Modifié le" value={fmtDate(product.updatedAt)} />}
                </div>

                {/* Active promo summary */}
                {productPromotions.filter(p => p.status === 'IN_PROGRESS').map(p => (
                  <div key={p.id} className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                    <CheckCircle size={18} className="text-green-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-green-800">{p.name}</p>
                      <p className="text-xs text-green-600">
                        +{p.commissionAmount} DH / scan · jusqu'au {fmtDate(p.endDate)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Promotions tab ───────────────────────────────────────────── */}
          {activeTab === 'promotions' && (
            <div>
              {productPromotions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <Tag size={32} className="mb-3 text-gray-300" />
                  <p className="text-sm font-medium">Aucune promotion</p>
                  <p className="text-xs mt-1">Ce produit n'est lié à aucune promotion.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {productPromotions.map(promo => {
                    const s = promoStatus(promo.status);
                    const now = new Date();
                    const ended = new Date(promo.endDate) < now;
                    return (
                      <div key={promo.id}
                        className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div>
                            <p className="font-semibold text-gray-900">{promo.name}</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {fmtDate(promo.startDate)} → {fmtDate(promo.endDate)}
                              {ended && <span className="text-red-500 ml-1">(expirée)</span>}
                            </p>
                          </div>
                          <Pill {...s} />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="bg-gray-50 rounded-xl p-3 text-center">
                            <p className="text-xs text-gray-400 mb-1">Commission</p>
                            <p className="font-bold text-gray-900 text-sm">
                              {promo.commissionType === 'PERCENTAGE'
                                ? `${promo.commissionAmount}%`
                                : `${promo.commissionAmount} DH`}
                            </p>
                          </div>
                          <div className="bg-gray-50 rounded-xl p-3 text-center">
                            <p className="text-xs text-gray-400 mb-1">Mazid</p>
                            <p className="font-bold text-gray-900 text-sm">{promo.commissionPercentage ?? '—'}%</p>
                          </div>
                          <div className="bg-gray-50 rounded-xl p-3 text-center">
                            <p className="text-xs text-gray-400 mb-1">QR codes</p>
                            <p className="font-bold text-gray-900 text-sm">{promo.quantity ?? '—'}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Ads tab ──────────────────────────────────────────────────── */}
          {activeTab === 'ads' && (
            <div>
              {activeAds.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <Zap size={32} className="mb-3 text-gray-300" />
                  <p className="text-sm font-medium">Aucune publicité active</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {activeAds.map(ad => (
                    <div key={ad.id} className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
                      <p className="font-semibold text-blue-900 mb-2">
                        Publicité · {ad.product?.name || product.name}
                      </p>
                      <div className="flex flex-wrap gap-4 text-sm text-blue-700">
                        <span className="flex items-center gap-1">
                          <Calendar size={13} />
                          {fmtDate(ad.dateDebut)} → {fmtDate(ad.dateFin)}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin size={13} />
                          {ad.laboratory?.nom ?? laboratory?.nom}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductViewModal;
